import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { TrackedLink } from "@/components/analytics/TrackedLink";
import LeadGenerationForm from "@/components/landing/LeadGenerationForm";

interface Stat {
  value: string;
  label: string;
}

const STATS: Stat[] = [
  { value: "150+", label: "Organizations covered" },
  { value: "99%", label: "Member renewal rate" },
];

// Radial mask fades the dot/grid texture out toward the bottom, matching the
// MembersSection treatment so the page reads as a single material.
const TEXTURE_MASK =
  "radial-gradient(ellipse 180% 100% at 50% 0%, black 0%, black 30%, transparent 85%)";

const LeadFormSection = () => {
  const textureRef = useRef<HTMLDivElement>(null);
  const revealRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  // Subtle page-scroll parallax for the dot/grid texture. Mirrors the
  // implementation in MembersSection so the two light sections feel of-a-piece.
  useEffect(() => {
    const node = textureRef.current;
    if (!node) return;
    const PARALLAX_FACTOR = 0.4;
    let rafId: number | null = null;
    const update = () => {
      rafId = null;
      const offsetY = -window.scrollY * PARALLAX_FACTOR;
      node.style.backgroundPosition = `45px ${offsetY}px, 0 ${45 + offsetY}px`;
    };
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // Slide the value-prop column in from the left and the form card in from the
  // right. The negative bottom rootMargin holds the reveal until the section
  // has scrolled well up into the viewport, so the columns float in later
  // (one-shot; respects reduced motion).
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setRevealed(true);
      return;
    }
    const node = revealRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          obs.disconnect();
        }
      },
      { threshold: 0, rootMargin: "0px 0px -35% 0px" },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="lead-form"
      className="relative overflow-hidden py-14 sm:py-20 md:py-32"
      style={{
        background: "linear-gradient(to bottom, #f4f4f5 0%, #ffffff 320px)",
      }}
    >
      {/* Dot matrix + grid lines with a radial fade-out mask. Sits behind all
          content via DOM order. Pointer-events disabled so it can't intercept
          clicks. */}
      <div
        ref={textureRef}
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(0,0,0,0.04) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0,0,0,0.04) 1px, transparent 1px)
          `,
          backgroundSize: "90px 90px, 90px 90px",
          backgroundPosition: "45px 0, 0 45px",
          backgroundRepeat: "repeat, repeat",
          backgroundAttachment: "fixed, fixed",
          maskImage: TEXTURE_MASK,
          WebkitMaskImage: TEXTURE_MASK,
        }}
      />

      {/* Soft cyan glow anchor behind the headline column. Adds a hint of brand
          warmth without competing with the form card. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-50"
        style={{ background: "radial-gradient(closest-side, rgba(68,194,227,0.18), transparent 70%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
        <div
          ref={revealRef}
          className="grid md:grid-cols-2 gap-12 md:gap-16 items-center"
        >
          {/* ── Left: value-prop column ───────────────────────────── */}
          <div
            className={`md:pr-4 transition-all duration-700 ease-out ${
              revealed ? "translate-x-0 opacity-100" : "-translate-x-12 opacity-0"
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur-sm border border-zinc-200/70 text-[11px] uppercase tracking-[0.18em] font-medium text-zinc-700">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan" />
              Get started
            </div>

            <h2 className="mt-5 text-3xl md:text-5xl font-black italic uppercase text-black leading-[1.05]">
              Earn Yours by Bringing Fivestar to Your Season
            </h2>
            <p className="mt-5 md:mt-6 text-base md:text-lg text-zinc-600 leading-relaxed max-w-md">
              Chat with us about your content strategy and we'll game-plan a membership package that's right for you.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              {STATS.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white/60 backdrop-blur-sm border border-zinc-200/60 rounded-xl p-4"
                >
                  <div className="text-3xl font-bold tracking-tight text-black leading-none">
                    {stat.value}
                  </div>
                  <div className="mt-2 text-[11px] uppercase tracking-wider text-zinc-500 font-medium leading-snug">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: form card ─────────────────────────────────── */}
          <div
            className={`bg-white rounded-2xl border border-zinc-200 shadow-[0_4px_30px_rgba(0,0,0,0.04)] p-6 md:p-8 transition-all duration-700 ease-out ${
              revealed ? "translate-x-0 opacity-100" : "translate-x-12 opacity-0"
            }`}
          >
            <LeadGenerationForm activeTab="member" isTabSwitcherHidden />
          </div>
        </div>

        {/* Secondary path: route photographers/videographers to the creator
            page. Rendered as a full-width band BELOW the grid so it sits after
            the form on mobile (where the columns stack) instead of wedged
            between the headline and the form fields, and reads as a centered
            secondary strip on desktop. Outline (not cyan-filled) so it never
            competes with the member "Request Info" CTA. */}
        <div className="mt-12 md:mt-16 pt-8 border-t border-zinc-200/70 flex flex-col items-center text-center">
          <p className="text-sm md:text-base text-zinc-700 leading-relaxed">
            Behind the camera? Get paid to shoot the events you love.
          </p>
          <TrackedLink
            ctaId="become_a_creator"
            ctaLocation="member_lead_form"
            to="/become-a-creator"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-zinc-300 px-5 py-2.5 text-sm font-semibold text-zinc-800 transition-colors hover:bg-zinc-100 hover:border-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2"
          >
            Become a Creator
            <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
          </TrackedLink>
        </div>
      </div>
    </section>
  );
};

export default LeadFormSection;
